import type { Building } from "building-types/building/buildingTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { type WallLayer } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { WallDatumAbstractionProvider } from "../elements/datums/datumAbstractionProviders";
import { groundLevelId } from "../elements/level/levelTools";
import { collectRoomConnectionsOnLevel } from "../elements/rooms/collectRoomConnectionsOnLevel";
import {
  subdomainHasFreeformWallParent,
  subdomainIsOrphanOrHasRoomVolumeParent,
  subdomainWallAssemblyOwnedByFreeform,
} from "../elements/walls2/wallPlaneSubdomainRoomFilter";
import { wallLayersFromWallMassSystems } from "../elements/walls/wallLayers";
import { makeWallMassSystem } from "../elements/walls/wallSystem";
import { getLevelInsideCornerHalfEdges } from "../generationTools/generateWallMassesTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { generateLevelWallMassesFromWallPlanes } from "./generateWallMasses2";

function select({
  levels,
  levelsAttributes,
  rooms,
  doc,
  roomLevelIndices,
  roomsByLevel,
  planarGraphs,
  openFullDoors,
  wallLayers,
  settings,
  planarGraphPerimeters,
  datumAbstractions,
  mirror,
  wallPaths,
  wallPlaneMasses,
  wallPlanes,
  buildingGraph,
  enabledFeatures,
}: Building) {
  return {
    levels,
    levelsAttributes,
    wallLayers,
    rooms,
    roomLevelIndices,
    roomsByLevel,
    planarGraphs,
    openFullDoors,
    wallSettings: settings.wall,
    wallCatalogSettings: settings.wallCatalog,
    planarGraphPerimeters,
    foundationSettings: settings.foundation,
    foundationComponents: doc.foundationComponents,
    foundationAssemblies: doc.foundationAssemblies,
    framingComponents: doc.framingComponents,
    slabComponents: doc.slabComponents,
    sheathingComponents: doc.sheathingComponents,
    freeformWalls: doc.freeformWalls ?? {},
    datumAbstractions,
    mirror,
    wallPaths,
    wallPlaneMasses,
    wallPlanes,
    buildingGraph,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  openFullDoors: "deep",
});

function generate({
  levels,
  levelsAttributes,
  wallLayers,
  rooms,
  roomsByLevel,
  roomLevelIndices,
  planarGraphs,
  openFullDoors,
  wallSettings,
  wallCatalogSettings,
  planarGraphPerimeters,
  foundationSettings,
  foundationComponents,
  framingComponents,
  slabComponents,
  sheathingComponents,
  freeformWalls,
  datumAbstractions,
  mirror,
  wallPaths,
  wallPlaneMasses,
  wallPlanes,
  buildingGraph,
  enabledFeatures,
}: ReturnType<typeof select>): {
  wallMasses: Table<WallMassSystem>;
  wallLayers: Table<WallLayer>;
  datumAbstractions: RWTable<DatumAbstraction>;
  issues: Building["issues"];
} {
  const issues: BuildingGenerationIssue[] = [];

  // Mass ownership follows who won SET_WALL_CORE on the UV cell (rule sortIndex), not
  // dual face-node attribution. Freeform-won overlap cells must not emit as room walls.
  const roomWallPlaneMasses = wallPlaneMasses.masses.filter((mass) => {
    const { subdomain } = mass.miterEdge;
    if (subdomainWallAssemblyOwnedByFreeform(subdomain, buildingGraph)) {
      return false;
    }
    return subdomainIsOrphanOrHasRoomVolumeParent(subdomain, buildingGraph);
  });
  const freeformWallPlaneMasses = wallPlaneMasses.masses.filter((mass) => {
    const { subdomain } = mass.miterEdge;
    if (!subdomainHasFreeformWallParent(subdomain, buildingGraph)) {
      return false;
    }
    // Pure freeform cells, or overlap cells where freeform rules won the core.
    return (
      subdomainWallAssemblyOwnedByFreeform(subdomain, buildingGraph) ||
      !subdomainIsOrphanOrHasRoomVolumeParent(subdomain, buildingGraph)
    );
  });

  if (enabledFeatures.wallPlanes) {
    return generateLevelWallMassesFromWallPlanes({
      levels,
      levelPlanarGraphs: planarGraphs,
      buildingGraph,
      wallPlaneMasses: roomWallPlaneMasses,
      freeformWallPlaneMasses,
      freeformWalls,
      wallPlanes,
      rooms,
      roomsByLevel,
      roomLevelIndices,
      issues,
    });
  }

  const allWallMasses = {};

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSystemSettingsFromSettingsConfig(foundationSettings),
  );

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);

  const makeLevelWallMassSystem = (levelId: string) => {
    const planarGraph = planarGraphs[levelId];

    const roomConnections = collectRoomConnectionsOnLevel({
      openFullDoors,
      levelId,
      roomsByLevel,
      roomLevelIndices,
    });

    const insideCornerHalfEdgeLookup = getLevelInsideCornerHalfEdges(planarGraphPerimeters, levelId);
    const levelWallPaths = wallPaths[levelId];
    if (!levelWallPaths) {
      throw new Error(`Wall paths not found for level ${levelId}`);
    }
    const wallMasses = makeWallMassSystem({
      rooms: roomsOnLevel(levelId, rooms, roomsByLevel),
      wallSettings,
      planarGraph,
      wallCatalogSettings,
      roomLevelIndices,
      framingComponents,
      slabComponents,
      sheathingComponents,
      roomConnections,
      wallDatumAbstractionProvider,
      levelId,
      levels,
      insideCornerHalfEdgeLookup,
      foundationComponents: finalizedFoundationComponents,
      mirror,
      wallPaths: levelWallPaths,
    });

    allWallMasses[levelId] = wallMasses;
  };

  // we need to draw the ground level wall masses first as reference
  const refLevelId = groundLevelId(levels);
  if (refLevelId) {
    makeLevelWallMassSystem(refLevelId);
  }

  for (const levelId in levels) {
    if (levelId !== refLevelId) {
      makeLevelWallMassSystem(levelId);
    }
  }

  const newWallLayers = wallLayersFromWallMassSystems(allWallMasses, wallLayers);

  return {
    wallMasses: allWallMasses,
    wallLayers: newWallLayers,
    /**
     * @NOTE - Generating wall masses mutates the datum abstractions table.
     * This is the final table that should be used for the rest of the generation process.
     */
    datumAbstractions: wallDatumAbstractionProvider.getDatumAbstractions(),
    issues,
  };
}

export default memo(select, generate, compare);
